import React, { useCallback, useMemo } from 'react';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { Container, useResponsive } from '@garnish/core';

import { GiftCard, useRedeemGiftCardDefaultValues } from '@order/features-gift-card';

import {
  AccountScreenTitle,
  ModalNavbarBackButton,
  SignedOutView,
  TransparentModal,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCustomerCredit } from '../../hooks';
import { addGiftCardScreenMessages as messages } from './AddGiftCardScreen.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const AddGiftCardScreen = () => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const params = useLocalSearchParams<{ giftCardId?: string }>();

  const { currentBreakpoint, match, minWidth } = useResponsive();
  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData: params.giftCardId,
  });
  const isLoggedIn = useIsLoggedIn();
  const { refetchCredits } = useCustomerCredit({
    requestPolicy: 'cache-and-network',
  });

  // ─── Flags ─────────────────────────────────────────────────────────────────

  // ─── Derived Data ────────────────────────────────────────────────────

  const screenTitle = formatMessage(messages.title);
  const redirectTo = useMemo<Href>(() => {
    const giftCardId = params.giftCardId;

    if (!giftCardId) {
      return minWidth.isSM ? '/account/gift-cards' : '/add-gift-card';
    }

    if (minWidth.isSM) {
      return `/account/gift-cards/${encodeURIComponent(giftCardId)}`;
    }

    return `/add-gift-card/${encodeURIComponent(giftCardId)}`;
  }, [minWidth.isSM, params.giftCardId]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    router.back();
  }, [router]);

  const handleRedeemSuccess = useCallback(() => {
    refetchCredits();

    router.dismissTo('/account/gift-cards', { withAnchor: true });
  }, [router, refetchCredits]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const contentContainerStyles = [
    containerStyles,
    currentBreakpoint.isXS ? styles.containerNavbarV2XS : undefined,
  ];
  const formStyles = match([styles.formContainerXS, styles.formContainerSM]);
  const modalMobileEdges = currentBreakpoint.isXS ? MODAL_SAFE_AREA_EDGES : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {isLoggedIn ? (
        <TransparentModal
          accessibilityLabel={screenTitle}
          size="full"
          mobileEdges={modalMobileEdges}
          onClose={handleClose}
          disableAnimation
        >
          <ScrollView>
            <Container wrapperStyle={containerStyles} style={contentContainerStyles}>
              <AccountScreenTitle title={screenTitle} />

              <View style={formStyles}>
                <GiftCard.RedemptionForm
                  style={styles.withoutBorder}
                  giftCardCode={defaultRedemptionValues?.giftCardCode}
                  giftCardRegistrationCode={defaultRedemptionValues?.giftCardRegistrationCode} // prettier-ignore
                  onRedeemSuccess={handleRedeemSuccess}
                />
              </View>
            </Container>
          </ScrollView>
        </TransparentModal>
      ) : (
        <SignedOutView redirectTo={redirectTo} withoutFooter />
      )}

      <ModalNavbarBackButton />
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MODAL_SAFE_AREA_EDGES: SafeAreaViewProps['edges'] = Platform.select({
  android: ['top', 'right', 'left'],
  default: ['right', 'left'],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerNavbarV2XS: {
    paddingTop: theme.spacing['4'],
  },
  containerSM: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingVertical: theme.spacing['8'],
  },
  formContainerXS: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  formContainerSM: {
    paddingVertical: theme.spacing['8'],
  },
  withoutBorder: {
    padding: 0,
    borderWidth: 0,
  },
});
